import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { styled, useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { invalidateUserSessionCredits } from "../../hooks/useAuth";
import { partyShellIdToModePathSegment } from "../../modes/shellToModeApi";
import { PartyTopBar } from "../../party/PartyChrome";
import { PartyMascot } from "../../party/PartyMascot";
import { handlePartyRosterNameEnter } from "../../party/partyNameInputEnter";
import { PartyPlayResumePrompt } from "../../party/PartyPlayResumePrompt";
import { PartySuggestCategoryDrawer } from "../../party/PartySuggestCategoryDrawer";
import { PartySavedRosterSetupSection } from "../../party/PartySavedRosterSetupSection";
import { getTrialQuickQuestionsDeck } from "../../party/partyTrialMockData";
import { PartyTrialSetupBanner } from "../../party/PartyTrialSetupBanner";
import PartyShareResultButton from "../../party/share/PartyShareResultButton";
import { usePartyShareModeTitle } from "../../party/share/usePartyShareModeTitle";
import { usePartyPlayTrialMode } from "../../party/usePartyPlayTrialMode";
import { usePartySavedRoster } from "../../party/usePartySavedRoster";
import type { PartyShellPalette } from "../../theme/theme";
import type { ModeCategoryRow, ModeQuickQuestionRow } from "../../types/api";
import { apiClient } from "../../utils/api";
import { trackPartyGameCompleted, trackPartyGameStarted } from "../../utils/mixpanel";
import { modeCategoryDisplayName, modeCategoryNamesFromIds } from "../../utils/modeCategoryLabels";
import { clearPartyGameCache, readPartyGameCache, writePartyGameCache } from "../../utils/partyGameLocalCache";

const SEG = partyShellIdToModePathSegment("fast");
const CACHE_SHELL = "fast";

type QuickQuestionsCacheData = {
  picked: number[];
  players: string[];
  deck: ModeQuickQuestionRow[];
  idx: number;
  showAnswer: boolean;
  scores: Record<number, number>;
};

type RankedRow = { name: string; i: number; sc: number; displayRank: number };

// --- styled -----------------------------------------------------------------

const SetupRoot = styled(Box)({
  padding: "4px 20px 100px",
});

const PlayRoot = styled(Box)({
  padding: "4px 20px 100px",
});

const EndRoot = styled(Box)({
  padding: "16px 20px 100px",
  textAlign: "center",
  minHeight: "100dvh",
  boxSizing: "border-box",
});

const HintText = styled(Typography)({
  display: "block",
  color: "var(--ink-dim)",
  fontSize: 14,
  marginBottom: 12,
  lineHeight: 1.5,
});

const NameRow = styled(Stack)({
  flexDirection: "row",
  gap: 8,
  marginBottom: 12,
});

const TeamField = styled(TextField)({
  flex: 1,
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "var(--ink)",
    "& fieldset": {
      borderColor: "var(--ink-faint)",
      borderWidth: 1.5,
    },
    "&.Mui-focused fieldset": {
      borderColor: "var(--hot)",
    },
  },
});

const PlayerChipsRow = styled(Stack)({
  flexDirection: "row",
  flexWrap: "wrap",
  gap: 6,
  marginBottom: 12,
});

const PlayerChip = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "6px 6px 6px 12px",
  borderRadius: 999,
  background: "var(--ink-faint)",
  fontWeight: 700,
  color: "var(--ink)",
});

const ChipRemove = styled(IconButton)({
  width: 28,
  height: 28,
  padding: 0,
  background: "rgba(255,255,255,0.1)",
  color: "var(--ink)",
  "&:hover": { background: "rgba(255,255,255,0.18)" },
});

const CategoryPickButton = styled("button", {
  shouldForwardProp: (p) => p !== "picked",
})<{ picked?: boolean }>(({ picked }) => ({
  width: "100%",
  marginBottom: 8,
  padding: "14px 16px",
  borderRadius: 16,
  font: "inherit",
  textAlign: "start",
  cursor: "pointer",
  fontWeight: 700,
  color: "var(--ink)",
  border: picked ? "2px solid var(--hot)" : "1.5px solid var(--ink-faint)",
  background: picked ? "rgba(255,61,120,0.1)" : "rgba(255,255,255,0.04)",
  "&:hover": {
    background: picked ? "rgba(255,61,120,0.14)" : "rgba(255,255,255,0.07)",
  },
}));

const LoadButton = styled(Button)({
  width: "100%",
  marginTop: 12,
});

const CreditErrorText = styled(Typography)({
  display: "block",
  color: "var(--hot)",
  marginTop: 8,
});

const QuestionCard = styled(Box)({
  "--card-bg": "var(--paper)",
  color: "var(--paper-ink)",
  padding: 20,
  marginBottom: 12,
});

const AnswerBlock = styled(Box)({
  marginTop: 16,
  paddingTop: 16,
  borderTop: "1px solid rgba(0,0,0,0.12)",
  fontWeight: 800,
  fontSize: 20,
});

const AwardHint = styled(Typography)({
  fontSize: 14,
  color: "var(--ink-dim)",
  marginBottom: 8,
});

const AwardStack = styled(Stack)({
  gap: 8,
});

const MascotWrap = styled(Box)({
  marginTop: 4,
  marginBottom: 12,
});

const ResultsKicker = styled(Box)({
  fontSize: 12,
  letterSpacing: "0.2em",
  color: "var(--hot)",
  fontWeight: 800,
  textShadow: "0 0 24px rgba(255, 61, 120, 0.35)",
});

const ResultsTitle = styled(Box)({
  fontSize: "clamp(30px, 9vw, 42px)",
  lineHeight: 1.05,
  marginTop: 8,
  color: "var(--ink)",
});

const ResultsSub = styled(Typography)({
  display: "block",
  marginTop: 10,
  color: "var(--ink-dim)",
  fontSize: 15,
  fontWeight: 600,
});

const ChampionZone = styled(Box)({
  marginTop: 22,
  marginBottom: 8,
  padding: "20px 16px 22px",
  borderRadius: 22,
  background: "linear-gradient(165deg, color-mix(in oklab, var(--gold) 18%, transparent), rgba(255,255,255,0.04))",
  border: "1.5px solid color-mix(in oklab, var(--gold) 45%, transparent)",
  boxShadow: "0 0 0 2px rgba(255, 200, 120, 0.25), 0 18px 48px -16px rgba(255, 200, 100, 0.22)",
});

const ChampionLabel = styled(Box)({
  fontSize: 11,
  letterSpacing: "0.16em",
  color: "var(--ink-dim)",
  marginBottom: 8,
});

const ChampionName = styled(Box)({
  fontFamily: "var(--font-display)",
  fontWeight: 900,
  fontSize: "clamp(28px, 8vw, 40px)",
  lineHeight: 1.1,
  color: "var(--gold)",
  textShadow: "0 0 40px rgba(255, 200, 100, 0.35)",
});

const ChampionScore = styled(Box)({
  marginTop: 8,
  fontFamily: "var(--font-display)",
  fontWeight: 900,
  fontSize: 44,
  color: "var(--mint)",
});

const ChampionNamesRow = styled(Stack)({
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "center",
  gap: 10,
  marginTop: 4,
});

const RunnerSection = styled(Box)({
  marginTop: 24,
  textAlign: "start",
  maxWidth: 420,
  marginLeft: "auto",
  marginRight: "auto",
});

const RunnerHeading = styled(Typography)({
  fontSize: 11,
  letterSpacing: "0.12em",
  color: "var(--ink-dim)",
  marginBottom: 10,
  textAlign: "center",
});

const RunnerRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "12px 14px",
  marginBottom: 8,
  borderRadius: 16,
  border: "1.5px solid var(--ink-faint)",
  background: "rgba(255,255,255,0.03)",
});

const RankBadge = styled(Box)({
  flexShrink: 0,
  minWidth: 36,
  height: 36,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "var(--font-mono)",
  fontWeight: 800,
  fontSize: 13,
  background: "rgba(255,255,255,0.06)",
  color: "var(--ink-dim)",
});

const RunnerName = styled(Typography)({
  flex: 1,
  fontWeight: 800,
  fontSize: 16,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const RunnerScore = styled(Typography)({
  fontFamily: "var(--font-display)",
  fontWeight: 900,
  fontSize: 22,
  color: "var(--ink)",
});

const HomeButton = styled(Button)({
  width: "100%",
  maxWidth: 420,
  marginTop: 28,
  marginLeft: "auto",
  marginRight: "auto",
  display: "block",
});

// --- helpers ----------------------------------------------------------------

function buildRankedLeaderboard(
  players: string[],
  scores: Record<number, number>,
): RankedRow[] {
  const sorted = players
    .map((name, i) => ({ name, i, sc: scores[i] ?? 0 }))
    .sort((a, b) => b.sc - a.sc);

  const out: RankedRow[] = [];
  let displayRank = 1;
  for (let i = 0; i < sorted.length; i++) {
    const row = sorted[i];
    if (i > 0 && row.sc < sorted[i - 1].sc) {
      displayRank = i + 1;
    }
    out.push({ ...row, displayRank });
  }
  return out;
}

function isTieAtTop(ranked: RankedRow[]): boolean {
  return ranked.filter((r) => r.displayRank === 1).length > 1;
}

// --- component --------------------------------------------------------------

export default function PartyPlayQuickQuestions() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const partyPal = (theme.palette as unknown as { party: PartyShellPalette }).party;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTrialMode = usePartyPlayTrialMode();
  const savedRoster = usePartySavedRoster();
  const shareModeTitle = usePartyShareModeTitle("fast", i18n.language?.startsWith("ar") ? "أسئلة سريعة" : "Quick questions");
  const [picked, setPicked] = useState<number[]>([]);
  const [players, setPlayers] = useState<string[]>([]);
  const [newName, setNewName] = useState("");
  const [deck, setDeck] = useState<ModeQuickQuestionRow[]>([]);
  const [idx, setIdx] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [scores, setScores] = useState<Record<number, number>>({});
  const [gameStartedAtMs, setGameStartedAtMs] = useState<number | null>(null);
  const [suggestCategoryOpen, setSuggestCategoryOpen] = useState(false);
  const completionTrackedRef = useRef(false);

  const playersRef = useRef(players);
  useEffect(() => {
    playersRef.current = players;
  }, [players]);

  const [cacheEnvelope] = useState(() => readPartyGameCache<QuickQuestionsCacheData>(CACHE_SHELL));
  const [resumeDismissed, setResumeDismissed] = useState(false);
  const cachedInProgress =
    cacheEnvelope != null &&
    Array.isArray(cacheEnvelope.data.deck) &&
    cacheEnvelope.data.deck.length > 0 &&
    typeof cacheEnvelope.data.idx === "number" &&
    cacheEnvelope.data.idx < cacheEnvelope.data.deck.length;
  const showResumePrompt = Boolean(cachedInProgress && !resumeDismissed);

  const catQ = useQuery({
    queryKey: ["modes", SEG, "categories"],
    queryFn: () => apiClient.getModeCategories(SEG),
    enabled: deck.length === 0 && !isTrialMode,
  });

  const startMut = useMutation({
    mutationFn: (ids: [number, number]) => apiClient.postQuickQuestionsStart(ids),
    onSuccess: (data: { questions_team_a?: ModeQuickQuestionRow[]; questions_team_b?: ModeQuickQuestionRow[] }, ids) => {
      savedRoster.recordPlayersBatch(playersRef.current);
      const a = data?.questions_team_a ?? [];
      const b = data?.questions_team_b ?? [];
      const out: ModeQuickQuestionRow[] = [];
      const n = Math.max(a.length, b.length);
      for (let i = 0; i < n; i++) {
        if (a[i]) out.push(a[i]);
        if (b[i]) out.push(b[i]);
      }
      setDeck(out);
      setIdx(0);
      setShowAnswer(false);
      setGameStartedAtMs(Date.now());
      completionTrackedRef.current = false;
      invalidateUserSessionCredits(queryClient);
      trackPartyGameStarted({
        shell_id: "fast",
        category_names: modeCategoryNamesFromIds([ids[0], ids[1]], catQ.data),
        number_of_players: players.length,
        trial: false,
        mode_name_en: "Quick questions",
        deck_size: out.length,
      });
    },
  });

  const beginQuickQuestionsTrial = useCallback(() => {
    savedRoster.recordPlayersBatch(playersRef.current);
    const out = getTrialQuickQuestionsDeck();
    setDeck(out);
    setIdx(0);
    setShowAnswer(false);
    setGameStartedAtMs(Date.now());
    completionTrackedRef.current = false;
    trackPartyGameStarted({
      shell_id: "fast",
      category_names: ["trial_sample"],
      number_of_players: players.length,
      trial: true,
      mode_name_en: "Quick questions",
      deck_size: out.length,
    });
  }, [savedRoster, players.length]);

  const card = deck[idx];

  const toggleCat = (id: number) => {
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [prev[0], id];
      return [...prev, id];
    });
  };

  const addPlayer = () => {
    const n = newName.trim();
    if (!n) return;
    setPlayers((p) => [...p, n]);
    setNewName("");
    savedRoster.recordPlayerAdded(n);
  };

  const removePlayerAt = (i: number) => setPlayers((p) => p.filter((_, j) => j !== i));

  const onExitPlayToSetup = useCallback(() => {
    clearPartyGameCache(CACHE_SHELL);
    setDeck([]);
  }, []);

  useEffect(() => {
    const onResults = deck.length > 0 && idx >= deck.length;
    if (onResults) {
      clearPartyGameCache(CACHE_SHELL);
      return;
    }
    const id = window.setTimeout(() => {
      const payload: QuickQuestionsCacheData = {
        picked,
        players,
        deck,
        idx,
        showAnswer,
        scores,
      };
      writePartyGameCache(CACHE_SHELL, payload);
    }, 400);
    return () => window.clearTimeout(id);
  }, [picked, players, deck, idx, showAnswer, scores]);

  useEffect(() => {
    const onResults = deck.length > 0 && idx >= deck.length;
    if (!onResults || gameStartedAtMs == null || completionTrackedRef.current) return;
    completionTrackedRef.current = true;
    trackPartyGameCompleted({
      shell_id: "fast",
      trial: isTrialMode,
      mode_name_en: "Quick questions",
      duration_seconds: Math.max(0, Math.round((Date.now() - gameStartedAtMs) / 1000)),
    });
  }, [deck.length, gameStartedAtMs, idx, isTrialMode]);

  const applyQuickQuestionsCache = useCallback((data: QuickQuestionsCacheData) => {
    setPicked(Array.isArray(data.picked) ? data.picked : []);
    setPlayers(Array.isArray(data.players) ? data.players : []);
    setDeck(Array.isArray(data.deck) ? data.deck : []);
    setIdx(typeof data.idx === "number" ? data.idx : 0);
    setShowAnswer(Boolean(data.showAnswer));
    setScores(data.scores && typeof data.scores === "object" ? data.scores : {});
  }, []);

  const goNextQuestion = () => {
    setIdx((i) => Math.min(i + 1, deck.length));
    setShowAnswer(false);
  };

  const award = (playerIndex: number) => {
    if (playerIndex >= 0) {
      setScores((s) => ({ ...s, [playerIndex]: (s[playerIndex] ?? 0) + 1 }));
    }
    goNextQuestion();
  };

  const ranked = useMemo(
    () => buildRankedLeaderboard(players, scores),
    [players, scores],
  );

  const champions = ranked.filter((r) => r.displayRank === 1);
  const runners = ranked.filter((r) => r.displayRank > 1);
  const tieAtTop = champions.length > 1;
  if (deck.length === 0) {
    return (
      <SetupRoot>
        <PartyTopBar title={t("party.playQuickQuestions.setupTitleBar")} onBack={() => navigate("/party/detail/fast")} />
        <Stack alignItems="center">
          <PartyMascot shellId="fast" size={96} />
        </Stack>
        <Typography className="party-display" sx={{ fontSize: 24, my: 1.5 }}>
          {t("party.playQuickQuestions.heading")}
        </Typography>
        {isTrialMode && <PartyTrialSetupBanner />}
        <HintText>{t("party.playQuickQuestions.setupHint")}</HintText>
        <NameRow>
          <TeamField
            size="small"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t("party.playQuickQuestions.playerPlaceholder")}
            onKeyDown={(e) => handlePartyRosterNameEnter(e, addPlayer)}
            hiddenLabel
            InputProps={{ "aria-label": t("party.playQuickQuestions.playerPlaceholder") }}
          />
          <IconButton
            type="button"
            onClick={addPlayer}
            aria-label={t("party.playQuickQuestions.addPlayerAria")}
            className="party-btn party-btn-paper"
            sx={{ borderRadius: 2, width: 48, height: 48, flexShrink: 0 }}
          >
            <AddIcon />
          </IconButton>
        </NameRow>
        <PlayerChipsRow>
          {players.map((p, i) => (
            <PlayerChip key={`${p}-${i}`}>
              {p}
              <ChipRemove
                type="button"
                size="small"
                aria-label={t("party.playQuickQuestions.removePlayerAria")}
                onClick={() => removePlayerAt(i)}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </ChipRemove>
            </PlayerChip>
          ))}
        </PlayerChipsRow>
        <PartySavedRosterSetupSection
          api={savedRoster}
          variant="players"
          currentPlayers={players}
          onQuickAddPlayer={(name) => {
            setPlayers((p) => [...p, name]);
            savedRoster.recordPlayerAdded(name);
          }}
        />
        {!isTrialMode &&
          (catQ.data ?? []).map((c: ModeCategoryRow) => (
            <CategoryPickButton key={c.id} type="button" picked={picked.includes(c.id)} onClick={() => toggleCat(c.id)}>
              {modeCategoryDisplayName(c, i18n.language)}
            </CategoryPickButton>
          ))}
        {!isTrialMode && (
          <>
            <Button
              type="button"
              variant="text"
              onClick={() => setSuggestCategoryOpen(true)}
              sx={{ mt: 1, color: partyPal.hot, fontWeight: 800, textTransform: "none", display: "block" }}
            >
              {t("party.suggestCategory.cta")}
            </Button>
            <PartySuggestCategoryDrawer
              open={suggestCategoryOpen}
              onClose={() => setSuggestCategoryOpen(false)}
              gameKey="quickQuestions"
              partyPal={partyPal}
            />
          </>
        )}
        {showResumePrompt && cacheEnvelope ? (
          <PartyPlayResumePrompt
            savedAtIso={cacheEnvelope.savedAt}
            onContinue={() => {
              applyQuickQuestionsCache(cacheEnvelope.data);
              setResumeDismissed(true);
            }}
            onNewGame={() => {
              clearPartyGameCache(CACHE_SHELL);
              setResumeDismissed(true);
            }}
          />
        ) : (
          <LoadButton
            type="button"
            disabled={
              isTrialMode
                ? players.length < 1
                : picked.length !== 2 || players.length < 1 || startMut.isPending
            }
            className="party-btn party-btn-hot"
            sx={{
              opacity:
                isTrialMode
                  ? players.length >= 1
                    ? 1
                    : 0.5
                  : picked.length === 2 && players.length >= 1
                    ? 1
                    : 0.5,
            }}
            onClick={() =>
              isTrialMode ? beginQuickQuestionsTrial() : startMut.mutate([picked[0], picked[1]] as [number, number])
            }
          >
            {isTrialMode ? t("party.trial.continueSampleCta") : t("party.playQuickQuestions.loadQuestions")}
          </LoadButton>
        )}
        {!isTrialMode && (startMut.error as { response?: { status?: number } })?.response?.status === 402 && (
          <CreditErrorText>{t("party.playQuickQuestions.creditInsufficient")}</CreditErrorText>
        )}
      </SetupRoot>
    );
  }

  if (idx >= deck.length) {
    return (
      <EndRoot>
        <PartyTopBar title={t("party.playQuickQuestions.endTitleBar")} onBack={() => navigate("/party/detail/fast")} />
        <MascotWrap>
          <PartyMascot shellId="fast" size={isTieAtTop(ranked) ? 100 : 120} />
        </MascotWrap>
        <ResultsKicker className="party-mono">{t("party.playQuickQuestions.resultsKicker")}</ResultsKicker>
        <ResultsTitle className="party-display party-anim-fade-scale-in">
          {t("party.playQuickQuestions.resultsTitle")}
        </ResultsTitle>
        <ResultsSub>{t("party.playQuickQuestions.resultsSub")}</ResultsSub>

        {ranked.length > 0 && (
          <ChampionZone className="party-anim-fade-scale-in">
            <ChampionLabel className="party-mono">
              {tieAtTop ? t("party.playQuickQuestions.championTie") : t("party.playQuickQuestions.championSolo")}
            </ChampionLabel>
            {tieAtTop ? (
              <ChampionNamesRow>
                {champions.map((c) => (
                  <ChampionName key={c.i}>{c.name}</ChampionName>
                ))}
              </ChampionNamesRow>
            ) : (
              <ChampionName>{champions[0]?.name}</ChampionName>
            )}
            <ChampionScore>
              {champions[0]?.sc ?? 0}{" "}
              {(champions[0]?.sc ?? 0) === 1 ? t("party.playQuickQuestions.point") : t("party.playQuickQuestions.points")}
            </ChampionScore>
          </ChampionZone>
        )}

        {runners.length > 0 && (
          <RunnerSection>
            <RunnerHeading className="party-mono">{t("party.playQuickQuestions.runnerUps")}</RunnerHeading>
            {runners.map((row) => (
              <RunnerRow key={row.i}>
                <RankBadge className="party-mono">{t("party.playQuickQuestions.rankHash", { rank: row.displayRank })}</RankBadge>
                <RunnerName>{row.name}</RunnerName>
                <RunnerScore>
                  {row.sc}{" "}
                  {row.sc === 1 ? t("party.playQuickQuestions.point") : t("party.playQuickQuestions.points")}
                </RunnerScore>
              </RunnerRow>
            ))}
          </RunnerSection>
        )}

        <PartyShareResultButton
          className="party-btn party-btn-paper"
          sx={{ width: "100%", maxWidth: 420, mt: 2 }}
          model={{
            shellId: "fast",
            modeTitle: shareModeTitle,
            resultTitle: t("party.playQuickQuestions.resultsTitle"),
            hero: tieAtTop
              ? champions.map((c) => c.name).join(i18n.language?.startsWith("ar") ? " · " : " · ")
              : champions[0]?.name ?? "",
            sub: `${champions[0]?.sc ?? 0} ${((champions[0]?.sc ?? 0) === 1 ? t("party.playQuickQuestions.point") : t("party.playQuickQuestions.points"))}`,
            standings: ranked.slice(0, 4).map((r, idx) => ({
              name: r.name,
              value: `${r.sc} ${r.sc === 1 ? t("party.playQuickQuestions.point") : t("party.playQuickQuestions.points")}`,
              highlight: idx === 0,
            })),
          }}
        />

        <HomeButton type="button" className="party-btn party-btn-hot" onClick={() => navigate("/")}>
          {t("party.playQuickQuestions.home")}
        </HomeButton>
      </EndRoot>
    );
  }

  return (
    <PlayRoot>
      <PartyTopBar
        title={t("party.playQuickQuestions.questionTopBar", { current: idx + 1, total: deck.length })}
        onBack={onExitPlayToSetup}
      />
      <QuestionCard className="party-kan-card party-anim-fade-scale-in">
        <Typography className="party-display" sx={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.25 }} component="div">
          {card?.question}
        </Typography>
        {showAnswer && (
          <AnswerBlock>
            {t("party.playQuickQuestions.answerLabel")}: {card?.answer}
          </AnswerBlock>
        )}
      </QuestionCard>
      {!showAnswer ? (
        <Button type="button" className="party-btn party-btn-hot" fullWidth sx={{ mb: 1.5 }} onClick={() => setShowAnswer(true)}>
          {t("party.playQuickQuestions.revealAnswer")}
        </Button>
      ) : (
        <>
          <AwardHint>{t("party.playQuickQuestions.whoAnsweredFirst")}</AwardHint>
          <AwardStack>
            {players.map((p, i) => (
              <Button key={i} type="button" className="party-btn party-btn-paper" fullWidth onClick={() => award(i)}>
                {t("party.playQuickQuestions.awardPlayer", { name: p })}
              </Button>
            ))}
          </AwardStack>
          <Button type="button" className="party-btn party-btn-ghost" fullWidth sx={{ mt: 1.5 }} onClick={() => goNextQuestion()}>
            {t("party.playQuickQuestions.skipNext")}
          </Button>
        </>
      )}
    </PlayRoot>
  );
}
